import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../Environment';
import useRetryFetch from '../../../hooks/useRetryFetch';
import LoggerContext from '../../../context/LoggerProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { Building2, Users, FolderTree, UserCog, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

interface OrganizationOverviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizations: Array<{
    _id: string;
    name: string;
  }>;
}

interface OverviewData {
  organizationId: string;
  organizationName: string;
  totalUsers: number;
  totalAdmins: number;
  totalCollections: number;
  collections: Array<{
    id: string;
    name: string;
    level: number;
    totalUsers: number;
    directUsers: number;
    mentors: Array<{
      id: string;
      email: string;
      displayName?: string;
      role: string;
    }>;
    subcollections: number;
  }>;
}

const OrganizationOverviewModal: React.FC<OrganizationOverviewModalProps> = ({
  open,
  onOpenChange,
  organizations,
}) => {
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const retryFetch = useRetryFetch();

  const [loading, setLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);

  const getLevelLabel = (level: number) => {
    if (level === 10) return t('department');
    if (level === 20) return t('course');
    if (level === 30) return t('class');
    return `${t('level')} ${level}`;
  };

  const fetchOverview = async (orgId: string) => {
    if (!orgId) return;

    try {
      setLoading(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/organization/${orgId}/report/overview`
      );

      if (response.ok) {
        const data = await response.json();
        setOverviewData(data);
      } else {
        toast.error(t('failed_to_generate_report'));
        setOverviewData(null);
      }
    } catch (error) {
      Logger.error('Failed to fetch organization overview:', error);
      toast.error(t('failed_to_generate_report'));
      setOverviewData(null);
    } finally {
      setLoading(false);
    }
  };

  // Set initial organization and fetch data
  useEffect(() => {
    if (open && organizations.length > 0 && !selectedOrgId) {
      const firstOrg = organizations[0]._id;
      setSelectedOrgId(firstOrg);
      fetchOverview(firstOrg);
    }
  }, [open, organizations]);

  // Fetch when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      fetchOverview(selectedOrgId);
    }
  }, [selectedOrgId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('organization_overview')}
          </DialogTitle>
        </DialogHeader>

        {/* Organization Selector */}
        {organizations.length > 1 && (
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('organization')}</label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org._id} value={org._id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3">{t('loading')}</span>
          </div>
        ) : overviewData ? (
          <div className="space-y-6">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    {t('total_users')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">
                    {overviewData.totalUsers}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <UserCog className="h-4 w-4 text-primary" />
                    {t('total_admins')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">
                    {overviewData.totalAdmins}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <FolderTree className="h-4 w-4 text-primary" />
                    {t('total_collections')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">
                    {overviewData.totalCollections}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    {t('total_mentors')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">
                    {overviewData.collections.reduce(
                      (acc, c) => acc + c.mentors.length,
                      0
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Collections Table */}
            <Card>
              <CardHeader>
                <CardTitle>{t('collections_breakdown')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('name')}</TableHead>
                        <TableHead>{t('level')}</TableHead>
                        <TableHead className="text-center">{t('direct_users')}</TableHead>
                        <TableHead className="text-center">{t('total_users')}</TableHead>
                        <TableHead className="text-center">{t('mentors')}</TableHead>
                        <TableHead className="text-center">{t('subcollections')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overviewData.collections.map((collection) => (
                        <TableRow key={collection.id}>
                          <TableCell className="font-medium">{collection.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getLevelLabel(collection.level)}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {collection.directUsers}
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {collection.totalUsers}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{collection.mentors.length}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {collection.subcollections}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Mentors Breakdown */}
            {overviewData.collections.some((c) => c.mentors.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('mentor_assignments')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {overviewData.collections
                      .filter((c) => c.mentors.length > 0)
                      .map((collection) => (
                        <div key={collection.id} className="border-b pb-4 last:border-b-0">
                          <h4 className="font-semibold text-sm mb-2">
                            {collection.name}{' '}
                            <Badge variant="outline" className="ml-2">
                              {getLevelLabel(collection.level)}
                            </Badge>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {collection.mentors.map((mentor) => (
                              <div
                                key={mentor.id}
                                className="flex items-center justify-between p-2 rounded bg-muted/50"
                              >
                                <div className="text-sm">
                                  <div className="font-medium">
                                    {mentor.displayName || mentor.email}
                                  </div>
                                  {mentor.displayName && (
                                    <div className="text-xs text-muted-foreground">
                                      {mentor.email}
                                    </div>
                                  )}
                                </div>
                                <Badge variant="secondary" className="text-xs">
                                  {mentor.role}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <Building2 className="mx-auto h-12 w-12 mb-2 opacity-50" />
            <p>{t('no_data_for_report')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationOverviewModal;
